/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { Checkbox, Stack } from "@fluentui/react";
import * as React from "react";

import { UserPersona } from "../../../../common/UserPersona";
import ShiftRequestStateIndicator from "../../../ShiftRequestStateIndicator";
import RequestConflict from "../../../conflict/RequestConflict";
import { ManagerResponse } from "../ManagerResponse/ManagerResponse";

import { getConflictProps, getContainerStackProps, getManagerResponseProps, getPersonaProps } from "./MemberRequest.mappers";
import { getClassNames } from "./MemberRequest.styles";
import { MemberRequestProps } from "./MemberRequestProps";
import { ShiftRequestStates } from "sh-models";
import { getStrings } from "@microsoft/shifts-experiences-shared";
import { ECSConfigKey, ECSConfigService } from "sh-services";

/**
 * Renders an individual member who is part of the shift request.
 * @param props Props for the component.
 * @returns An element of the member.
 */
export const MemberRequest: React.FC<MemberRequestProps> = props => {
    const { request, onClickRequestCallback, isSelectionEnabled, requestCount } = props;
    const { senderMember, isSelected, request: requestDetails, managerMember } = request;
    const { creationTime, id, state } = requestDetails;

    const styles = getClassNames();

    const isToggleCheckboxVisibilityEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.ToggleCheckboxVisibilityEnabled);
    const personaProps = getPersonaProps(senderMember, creationTime);
    const unknownUserString = getStrings("openShiftsExperience", "unavailableUserNameText");
    const containerStackProps = getContainerStackProps(onClickRequestCallback, isSelected, personaProps?.text ?? unknownUserString, id, ...(isToggleCheckboxVisibilityEnabled ? [requestCount] : []));
    const managerResponseProps = getManagerResponseProps(requestDetails, managerMember);
    const conflictProps = getConflictProps(requestDetails);
    const isRequestCancelledBySender = request.request.state === ShiftRequestStates.SenderDeclined;

    return (
        <Stack className={styles.stack} {...containerStackProps}>
            <Stack horizontal horizontalAlign="space-between">
                {personaProps ? <UserPersona {...personaProps} /> : null}
                {isSelectionEnabled ?
                ((!isToggleCheckboxVisibilityEnabled || requestCount > 1) && (<Checkbox ariaLabel={senderMember.displayName} checked={isSelected} />)) : (<ShiftRequestStateIndicator shiftRequestState={state} />)}
            </Stack>
            <Stack.Item>
                <Stack.Item>{conflictProps ? <RequestConflict {...conflictProps} /> : null}</Stack.Item>
                {!isRequestCancelledBySender && <Stack.Item>
                    {managerResponseProps ? <ManagerResponse {...managerResponseProps} /> : null}
                </Stack.Item>}
            </Stack.Item>
        </Stack>
    );
};
